/**
 * SfxManager — singleton for one-shot SFX (Fase 0)
 *
 * Responsibilities:
 *  - Owns the Web Audio graph for SFX (master gain → destination).
 *  - Lazily creates / resumes the AudioContext inside a user gesture.
 *  - Optionally shares an existing AudioContext (e.g. the one owned by
 *    HandsFreeProvider) so SFX + voice go through the same graph and
 *    benefit from the same AEC.
 *  - Respects a persisted mute preference (sfxPreference.ts).
 *  - Surfaces a no-op API on environments without Web Audio (SSR, very
 *    old browsers) so callers can `play()` unconditionally.
 *
 * Lifecycle:
 *  1. `getSfxManager()` returns the singleton (created on first call).
 *  2. `unlock()` MUST be called from a user gesture (click handler) before
 *     the first `play()` — this is what satisfies the browser autoplay
 *     policy.
 *  3. `play(id)` schedules the SFX and returns immediately. Sound is
 *     non-blocking and never throws into the UI.
 *  4. `dispose()` on app teardown (rare) — releases the master gain.
 *
 * Threading: all methods are synchronous. Audio scheduling uses the
 * AudioContext's internal clock, not the JS event loop.
 */

import { SFX_RECIPES, type SfxId } from './sfxCatalog';
import { getSfxMuted, setSfxMuted as persistSfxMuted } from './sfxPreference';

/** Default master gain (linear). 0.6 keeps SFX below the TTS voice. */
const DEFAULT_MASTER_GAIN = 0.6;

class SfxManager {
  private ctx: AudioContext | null = null;
  /** External context (from HandsFreeProvider). Used in preference over our own. */
  private externalCtx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private muted = false;
  private disposed = false;

  constructor() {
    this.muted = getSfxMuted();
  }

  // ── Public API ─────────────────────────────────────────────────────────

  /**
   * Bind to an existing AudioContext (e.g. the one HandsFreeProvider
   * already created). When bound, the SfxManager uses that context
   * instead of creating its own. Call this BEFORE `unlock()` if at all —
   * once the internal context is created, switching is a no-op.
   */
  setAudioContext(ctx: AudioContext | null): void {
    if (!ctx) return;
    if (this.ctx) return; // already have one
    this.externalCtx = ctx;
    this.ctx = ctx;
  }

  /**
   * Unlock the AudioContext. MUST be called from a user gesture (e.g.
   * a click handler). After this resolves, `play()` is allowed.
   *
   * Idempotent: if already unlocked, this is a cheap no-op that just
   * ensures the context is `running`.
   */
  async unlock(): Promise<void> {
    if (this.disposed) return;
    const ctx = this.ensureContext();
    if (!ctx) return; // no Web Audio — silently no-op
    if (ctx.state === 'suspended') {
      try {
        await ctx.resume();
      } catch (err) {
        // Autoplay policy can still block in edge cases; just log and move on.
        // Don't throw — the UI must not break because of audio.
        console.warn('[🔊 SFX] Failed to resume AudioContext:', err);
      }
    }
  }

  /**
   * Play an SFX. Returns true if it was scheduled, false if it was
   * skipped (muted, no context, unknown id). Never throws.
   */
  play(id: SfxId, opts: { volume?: number } = {}): boolean {
    if (this.disposed) return false;
    if (this.muted) return false;
    const ctx = this.ctx;
    const master = this.masterGain;
    if (!ctx || !master) return false;
    const recipe = SFX_RECIPES[id];
    if (!recipe) {
      console.warn(`[🔊 SFX] Unknown SFX id: ${id}`);
      return false;
    }
    try {
      // Resume if the context was suspended (e.g. tab was backgrounded
      // mid-session). Cheap; no-op if already running.
      if (ctx.state === 'suspended') {
        void ctx.resume();
      }
      const vol = Math.max(0, Math.min(1, opts.volume ?? 1));
      recipe(ctx, master, vol);
      return true;
    } catch (err) {
      console.warn(`[🔊 SFX] play(${id}) failed:`, err);
      return false;
    }
  }

  /** Set mute at runtime. Persists across reloads. */
  setMuted(muted: boolean): void {
    this.muted = muted;
    persistSfxMuted(muted);
  }

  /** Read current mute state. */
  isMuted(): boolean {
    return this.muted;
  }

  /** Has the AudioContext been created and (hopefully) resumed? */
  isUnlocked(): boolean {
    return this.ctx !== null && this.ctx.state === 'running';
  }

  /** Tear down. Idempotent. */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    try {
      this.masterGain?.disconnect();
    } catch {
      /* ignore */
    }
    this.masterGain = null;
    // Don't close the AudioContext if we borrowed it from elsewhere.
    if (!this.externalCtx) {
      void this.ctx?.close();
    }
    this.ctx = null;
    this.externalCtx = null;
  }

  // ── Internals ──────────────────────────────────────────────────────────

  private ensureContext(): AudioContext | null {
    if (this.ctx && this.masterGain) return this.ctx;
    if (typeof window === 'undefined') return null;
    const Ctor = window.AudioContext || (window as any).webkitAudioContext;
    if (!Ctor) return null;
    // If we were given an external context but masterGain was lost (e.g.
    // after a disconnect), recreate the gain only.
    if (this.ctx) {
      this.masterGain = this.ctx.createGain();
      this.masterGain.gain.value = DEFAULT_MASTER_GAIN;
      this.masterGain.connect(this.ctx.destination);
      return this.ctx;
    }
    try {
      this.ctx = new Ctor({ latencyHint: 'interactive' });
      this.masterGain = this.ctx.createGain();
      this.masterGain.gain.value = DEFAULT_MASTER_GAIN;
      this.masterGain.connect(this.ctx.destination);
      return this.ctx;
    } catch (err) {
      console.warn('[🔊 SFX] Failed to create AudioContext:', err);
      this.ctx = null;
      this.masterGain = null;
      return null;
    }
  }
}

// ── Singleton accessor ────────────────────────────────────────────────────

let instance: SfxManager | null = null;

/**
 * Get the process-wide SfxManager. Lazily constructed. Safe to call from
 * SSR / prerender (returns an object whose methods are no-ops there).
 */
export function getSfxManager(): SfxManager {
  if (!instance) {
    instance = new SfxManager();
  }
  return instance;
}

/** For tests / hot-reload teardown. */
export function _resetSfxManagerForTesting(): void {
  instance?.dispose();
  instance = null;
}
